import { AccessibilitySystem, Application, Container, extensions, Graphics } from 'pixi.js';
import { arrowHead, type ArrowData } from '../model/arrow';
import { buildArrowCellMap, cellIndex, type BoardData } from '../model/board';
import {
  arrowPolyline,
  exitPolyline,
  headGeometry,
  polylineLength,
  subPolyline,
  STROKE_RATIO,
  type Point,
} from './arrowGeometry';
import { BoardCamera, CELL_SIZE } from './camera';
import { LIGHT_THEME, type RenderTheme } from './theme';

/**
 * PixiJS board scene.
 *
 * Winding arrows cannot share a single sprite texture, so instead of one display object per
 * arrow the whole resting board is stroked into *one* Graphics geometry, rebuilt only when
 * the set of resting arrows changes. Arrows that are moving or flashing are drawn into a
 * second, small Graphics refreshed per frame. A board of any size therefore costs a handful
 * of draw calls, and nothing is allocated per arrow per frame.
 *
 * Hit testing never touches display objects: a pointer position becomes a cell index which
 * is looked up in a flat Int32Array covering every cell of every path, so tapping anywhere
 * along a long arrow selects it.
 */

export interface BoardSceneOptions {
  readonly theme?: RenderTheme;
  readonly reducedMotion?: boolean;
  readonly showGrid?: boolean;
  onContextLost?(): void;
}

/** Bounding box of the active silhouette, in cells. */
function activeBounds(board: BoardData): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  let minX = board.width;
  let minY = board.height;
  let maxX = 0;
  let maxY = 0;
  for (let y = 0; y < board.height; y += 1) {
    for (let x = 0; x < board.width; x += 1) {
      if (board.activeMask[cellIndex(board, x, y)] !== 1) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < minX) return { minX: 0, minY: 0, maxX: board.width - 1, maxY: board.height - 1 };
  return { minX, minY, maxX, maxY };
}

export class WebGLUnavailableError extends Error {
  constructor(cause: unknown) {
    super(`Could not start the graphics renderer: ${String(cause)}`);
    this.name = 'WebGLUnavailableError';
  }
}

/**
 * Playback speed of the arrow exit slide, as a multiple of the base timing below. Early
 * players found the exit too fast, so it runs at half speed; lower is slower. Purely
 * cosmetic — it changes only how the removal animates, never the board or the rules.
 */
const EXIT_SPEED = 0.6;

/** Base exit-slide duration in ms, before {@link EXIT_SPEED}. Reduced-motion uses a shorter one. */
const EXIT_DURATION_MS = 300;
const EXIT_DURATION_REDUCED_MS = 130;

interface ExitAnimation {
  readonly points: Point[];
  readonly bodyLength: number;
  readonly runwayLength: number;
  elapsed: number;
  readonly duration: number;
  /** Called once when the slide finishes, so the win can be finalised only after the arrow leaves. */
  readonly onComplete?: (() => void) | undefined;
}

interface Flash {
  readonly arrow: ArrowData;
  readonly colour: number;
  readonly shake: boolean;
  elapsed: number;
  readonly duration: number;
}

/*
 * The board exposes its own accessible interface: a focusable container with a described
 * focus ring, keyboard navigation over logical arrow data and live announcements. PixiJS's
 * accessibility system would additionally inject an unlabelled focusable button into the
 * document on touch devices, which is a defect on top of an interface that already works,
 * so the extension is removed rather than configured.
 */
extensions.remove(AccessibilitySystem);

export class BoardScene {
  readonly camera = new BoardCamera();

  private readonly app: Application;
  private readonly world = new Container();
  private readonly surfaceLayer = new Graphics();
  private readonly gridLayer = new Graphics();
  private readonly highlightLayer = new Graphics();
  private readonly arrowLayer = new Graphics();
  private readonly headLayer = new Graphics();
  private readonly effectLayer = new Graphics();
  private readonly host: HTMLElement;

  private board: BoardData | null = null;
  private cellToArrow: Int32Array<ArrayBufferLike> = new Int32Array(0);
  private alive = new Set<number>();
  private readonly exits: ExitAnimation[] = [];
  private readonly flashes = new Map<number, Flash>();

  private theme: RenderTheme;
  private showGrid: boolean;
  private reducedMotion: boolean;
  private hintArrowId = -1;
  private focusArrowId = -1;
  private disposed = false;
  private resizeObserver: ResizeObserver | null = null;
  private readonly onContextLost: (() => void) | undefined;

  private constructor(app: Application, host: HTMLElement, options: BoardSceneOptions) {
    this.app = app;
    this.host = host;
    this.theme = options.theme ?? LIGHT_THEME;
    this.showGrid = options.showGrid ?? true;
    this.reducedMotion = options.reducedMotion ?? false;
    this.onContextLost = options.onContextLost;

    this.world.addChild(
      this.surfaceLayer,
      this.gridLayer,
      this.highlightLayer,
      this.arrowLayer,
      this.headLayer,
      this.effectLayer,
    );
    this.app.stage.addChild(this.world);

    this.app.ticker.add((ticker) => this.update(ticker.deltaMS));
    this.app.canvas.addEventListener('webglcontextlost', this.handleContextLost);
    this.observeResize();
  }

  static async create(host: HTMLElement, options: BoardSceneOptions = {}): Promise<BoardScene> {
    const app = new Application();
    const theme = options.theme ?? LIGHT_THEME;
    try {
      await app.init({
        antialias: true,
        backgroundAlpha: 0,
        background: theme.background,
        resolution: Math.min(globalThis.devicePixelRatio ?? 1, 2),
        autoDensity: true,
        powerPreference: 'high-performance',
        resizeTo: host,
      });
    } catch (error) {
      throw new WebGLUnavailableError(error);
    }

    app.canvas.style.display = 'block';
    app.canvas.style.touchAction = 'none';
    host.appendChild(app.canvas);

    const scene = new BoardScene(app, host, options);
    scene.resize();
    return scene;
  }

  get canvas(): HTMLCanvasElement {
    return this.app.canvas;
  }

  /** Arrows currently drawn on the board. Must always match the session's alive set. */
  get renderedArrowCount(): number {
    return this.alive.size;
  }

  private handleContextLost = (event: Event): void => {
    event.preventDefault();
    this.onContextLost?.();
  };

  private observeResize(): void {
    if (typeof ResizeObserver === 'undefined') return;
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.host);
  }

  setBoard(board: BoardData, aliveIds?: readonly number[]): void {
    this.board = board;
    this.cellToArrow = buildArrowCellMap(board);
    this.alive = new Set(aliveIds ?? board.arrows.map((arrow) => arrow.id));
    this.exits.length = 0;
    this.flashes.clear();
    this.hintArrowId = -1;
    this.focusArrowId = -1;

    this.camera.setBoardSize(board.width, board.height);
    // Frame the silhouette rather than the whole grid: a stencil rarely fills its bounding
    // box, and the difference is most of the screen on a phone.
    const bounds = activeBounds(board);
    this.camera.setContentBounds(
      bounds.minX,
      bounds.minY,
      bounds.maxX - bounds.minX + 1,
      bounds.maxY - bounds.minY + 1,
    );
    this.camera.fit();
    this.applyCamera();

    this.drawBackdrop();
    this.drawArrows();
    this.drawHighlights();
    this.effectLayer.clear();
  }

  // ------------------------------------------------------------------ drawing

  private get strokeWidth(): number {
    return CELL_SIZE * STROKE_RATIO;
  }

  /** Silhouette surface plus the optional dot grid; redrawn only on board or theme change. */
  private drawBackdrop(): void {
    this.surfaceLayer.clear();
    this.gridLayer.clear();
    const board = this.board;
    if (board === null) return;

    for (let y = 0; y < board.height; y += 1) {
      // Merge horizontal runs of active cells into single rectangles: far fewer geometry
      // commands than one shape per cell, with the same appearance.
      let runStart = -1;
      for (let x = 0; x <= board.width; x += 1) {
        const active = x < board.width && board.activeMask[cellIndex(board, x, y)] === 1;
        if (active && runStart < 0) runStart = x;
        if (!active && runStart >= 0) {
          this.surfaceLayer.rect(
            runStart * CELL_SIZE,
            y * CELL_SIZE,
            (x - runStart) * CELL_SIZE,
            CELL_SIZE,
          );
          runStart = -1;
        }
      }
    }
    this.surfaceLayer.fill({ color: this.theme.boardSurface });
    this.drawGrid();
  }

  /**
   * Dots on the cells a player can actually see.
   *
   * A dot under an arrow is hidden by it, and boards now run above ninety per cent covered,
   * so drawing the full grid meant tessellating around two thousand circles to show maybe a
   * hundred — over half the frame cost of a dense board. Redrawn with the arrows, so vacated
   * cells get their dot back as the board empties.
   */
  private drawGrid(): void {
    this.gridLayer.clear();
    const board = this.board;
    if (board === null || !this.showGrid) return;

    const covered = new Uint8Array(board.width * board.height);
    for (const arrow of board.arrows) {
      if (!this.alive.has(arrow.id)) continue;
      for (const cell of arrow.cells) covered[cellIndex(board, cell.x, cell.y)] = 1;
    }

    const radius = CELL_SIZE * 0.045;
    let any = false;
    for (let y = 0; y < board.height; y += 1) {
      for (let x = 0; x < board.width; x += 1) {
        const index = cellIndex(board, x, y);
        if (board.activeMask[index] !== 1 || covered[index] === 1) continue;
        this.gridLayer.circle(x * CELL_SIZE + CELL_SIZE / 2, y * CELL_SIZE + CELL_SIZE / 2, radius);
        any = true;
      }
    }
    if (any) this.gridLayer.fill({ color: this.theme.gridDot, alpha: this.theme.gridDotAlpha });
  }

  /**
   * Strokes every resting arrow into one geometry, as *two* draw instructions.
   *
   * Every resting arrow shares a colour and width, so all the shafts accumulate into a single
   * path closed by one `stroke()`, and all the chevrons into one path closed by one `fill()`.
   * Stroking each arrow separately instead costs two instructions per arrow — on a
   * two-hundred-arrow board that was four hundred, and it showed up as a frame-time floor
   * that no amount of zooming out relieved.
   */
  private drawArrows(): void {
    this.arrowLayer.clear();
    this.headLayer.clear();
    const board = this.board;
    if (board === null) return;

    let hasShaft = false;
    let hasHead = false;

    for (const arrow of board.arrows) {
      if (!this.alive.has(arrow.id) || this.flashes.has(arrow.id)) continue;
      const points = arrowPolyline(arrow);
      if (points.length < 2) continue;

      const head = headGeometry(points);
      const shaftEnd = head?.shaftEnd ?? points[points.length - 1];

      this.arrowLayer.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length - 1; i += 1) {
        this.arrowLayer.lineTo(points[i].x, points[i].y);
      }
      this.arrowLayer.lineTo(shaftEnd.x, shaftEnd.y);
      hasShaft = true;

      if (head !== null) {
        this.headLayer
          .moveTo(head.tip.x, head.tip.y)
          .lineTo(head.left.x, head.left.y)
          .lineTo(head.right.x, head.right.y)
          .closePath();
        hasHead = true;
      }
    }

    if (hasShaft) {
      this.arrowLayer.stroke({
        width: this.strokeWidth,
        color: this.theme.arrow,
        cap: 'round',
        join: 'round',
      });
    }
    if (hasHead) this.headLayer.fill({ color: this.theme.arrow });

    this.drawGrid();
  }

  private drawHighlights(): void {
    this.highlightLayer.clear();
    const board = this.board;
    if (board === null) return;

    const highlightWidth = this.strokeWidth * 2.4;
    const targets: readonly (readonly [number, number])[] = [
      [this.hintArrowId, this.theme.hint],
      [this.focusArrowId, this.theme.focus],
    ];

    for (const [arrowId, colour] of targets) {
      if (arrowId < 0 || !this.alive.has(arrowId)) continue;
      const arrow = board.arrows[arrowId];
      if (arrow === undefined) continue;

      const points = arrowPolyline(arrow);
      if (points.length < 2) continue;
      this.highlightLayer.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i += 1) {
        this.highlightLayer.lineTo(points[i].x, points[i].y);
      }
      this.highlightLayer.stroke({
        width: highlightWidth,
        color: colour,
        alpha: this.theme.plateAlpha,
        cap: 'round',
        join: 'round',
      });
    }
  }

  /** Draws one arrow: rounded shaft plus chevron head. */
  private strokeArrow(
    graphics: Graphics,
    points: readonly Point[],
    colour: number,
    alpha: number,
    offsetX = 0,
  ): void {
    // Fewer than two points cannot describe a direction, and a bare dot would read as a
    // mystery object the player cannot solve. Draw nothing instead.
    if (points.length < 2) return;
    const width = this.strokeWidth;

    const head = headGeometry(points);
    const shaftEnd = head?.shaftEnd ?? points[points.length - 1];

    graphics.moveTo(points[0].x + offsetX, points[0].y);
    for (let i = 1; i < points.length - 1; i += 1) {
      graphics.lineTo(points[i].x + offsetX, points[i].y);
    }
    graphics.lineTo(shaftEnd.x + offsetX, shaftEnd.y);
    graphics.stroke({ width, color: colour, alpha, cap: 'round', join: 'round' });

    if (head !== null) {
      graphics
        .moveTo(head.tip.x + offsetX, head.tip.y)
        .lineTo(head.left.x + offsetX, head.left.y)
        .lineTo(head.right.x + offsetX, head.right.y)
        .closePath()
        .fill({ color: colour, alpha });
    }
  }

  // ---------------------------------------------------------------- animation

  private update(deltaMs: number): void {
    if (this.exits.length === 0 && this.flashes.size === 0) return;

    this.effectLayer.clear();
    let flashFinished = false;

    for (let i = this.exits.length - 1; i >= 0; i -= 1) {
      const exit = this.exits[i];
      exit.elapsed += deltaMs;
      const progress = Math.min(1, exit.elapsed / exit.duration);
      // Ease-in: the arrow accelerates away, which reads as leaving rather than drifting.
      const eased = progress * progress * progress;
      // The head starts at its rest position (bodyLength along the path) and advances by the
      // runway. Starting from 0 instead grew the body out of its own tail — a spring-like
      // compression before it moved; this keeps the arrow rigid and moving from frame one.
      const travelled = exit.bodyLength + eased * exit.runwayLength;

      const window = subPolyline(exit.points, travelled - exit.bodyLength, travelled);
      if (window.length > 0) {
        const fade = progress > 0.7 ? Math.max(0, 1 - (progress - 0.7) / 0.3) : 1;
        this.strokeArrow(this.effectLayer, window, this.theme.arrow, fade);
      }
      if (progress >= 1) {
        this.exits.splice(i, 1);
        exit.onComplete?.();
      }
    }

    for (const [arrowId, flash] of this.flashes) {
      flash.elapsed += deltaMs;
      const progress = Math.min(1, flash.elapsed / flash.duration);
      if (progress >= 1) {
        this.flashes.delete(arrowId);
        flashFinished = true;
        continue;
      }

      // Three decaying oscillations, computed without trigonometry.
      const offset = flash.shake
        ? (Math.abs(((progress * 6) % 2) - 1) * 2 - 1) * (1 - progress) * (CELL_SIZE * 0.14)
        : 0;
      const alpha = flash.shake
        ? 1
        : 1 - (progress < 0.5 ? progress * 2 : (1 - progress) * 2) * 0.5;
      this.strokeArrow(this.effectLayer, arrowPolyline(flash.arrow), flash.colour, alpha, offset);
    }

    if (flashFinished) this.drawArrows();
  }

  // ------------------------------------------------------------------- input

  /** Arrow id at a screen position, or -1. */
  hitTest(screenX: number, screenY: number): number {
    const board = this.board;
    if (board === null) return -1;
    const cell = this.camera.screenToCell(screenX, screenY);
    if (cell === null) return -1;
    const arrowId = this.cellToArrow[cell.row * board.width + cell.column];
    if (arrowId < 0 || !this.alive.has(arrowId)) return -1;
    return arrowId;
  }

  /** Screen rectangle of an arrow's head cell, for the DOM focus ring. */
  cellScreenPosition(arrowId: number): { x: number; y: number; size: number } | null {
    const board = this.board;
    if (board === null) return null;
    const arrow = board.arrows[arrowId];
    if (arrow === undefined) return null;
    const head = arrowHead(arrow);
    const point = this.camera.worldToScreen(head.x * CELL_SIZE, head.y * CELL_SIZE);
    return { x: point.x, y: point.y, size: this.camera.cellScreenSize };
  }

  // ------------------------------------------------------------------ states

  pressArrow(arrowId: number): void {
    const board = this.board;
    if (board === null || !this.alive.has(arrowId)) return;
    const arrow = board.arrows[arrowId];
    if (arrow === undefined) return;

    this.flashes.set(arrowId, {
      arrow,
      colour: this.theme.arrowPressed,
      shake: false,
      elapsed: 0,
      duration: 160,
    });
    this.drawArrows();
  }

  /**
   * Plays the exit animation; the arrow is already gone from the logical board. `onComplete`
   * runs once the slide finishes — used to hold the win dialog until the arrow has left. If
   * there is nothing to animate it still runs, so a caller waiting on it can never be stranded.
   */
  removeArrow(arrowId: number, onComplete?: () => void): void {
    const board = this.board;
    if (board === null || !this.alive.has(arrowId)) {
      onComplete?.();
      return;
    }
    const arrow = board.arrows[arrowId];
    if (arrow === undefined) {
      onComplete?.();
      return;
    }

    this.alive.delete(arrowId);
    this.flashes.delete(arrowId);

    const points = exitPolyline(arrow, board.width, board.height);
    const bodyLength = polylineLength(arrowPolyline(arrow));
    this.exits.push({
      points,
      bodyLength,
      runwayLength: polylineLength(points) - bodyLength,
      elapsed: 0,
      // A constant duration regardless of path length or board size: the feedback should
      // feel the same for a one-cell arrow and a fourteen-cell one.
      duration: (this.reducedMotion ? EXIT_DURATION_REDUCED_MS : EXIT_DURATION_MS) / EXIT_SPEED,
      onComplete,
    });

    if (this.hintArrowId === arrowId) this.hintArrowId = -1;
    if (this.focusArrowId === arrowId) this.focusArrowId = -1;

    this.drawArrows();
    this.drawHighlights();
  }

  rejectArrow(arrowId: number, blockerId: number, flashBlocker: boolean): void {
    const board = this.board;
    if (board === null) return;
    const arrow = board.arrows[arrowId];
    if (arrow === undefined || !this.alive.has(arrowId)) return;

    this.flashes.set(arrowId, {
      arrow,
      colour: this.theme.arrowBlocked,
      // Reduced motion swaps the shake for a pulse: same information, no movement.
      shake: !this.reducedMotion,
      elapsed: 0,
      duration: 420,
    });

    if (flashBlocker && blockerId >= 0 && blockerId !== arrowId) {
      const blocker = board.arrows[blockerId];
      if (blocker !== undefined && this.alive.has(blockerId)) {
        this.flashes.set(blockerId, {
          arrow: blocker,
          colour: this.theme.arrowBlocked,
          shake: false,
          elapsed: 0,
          duration: 420,
        });
      }
    }
    this.drawArrows();
  }

  setHint(arrowId: number): void {
    if (this.hintArrowId === arrowId) return;
    this.hintArrowId = arrowId;
    this.drawHighlights();
  }

  setFocus(arrowId: number): void {
    if (this.focusArrowId === arrowId) return;
    this.focusArrowId = arrowId;
    this.drawHighlights();
  }

  setTheme(theme: RenderTheme): void {
    this.theme = theme;
    this.app.renderer.background.color = theme.background;
    this.drawBackdrop();
    this.drawArrows();
    this.drawHighlights();
  }

  setReducedMotion(reduced: boolean): void {
    this.reducedMotion = reduced;
  }

  setShowGrid(show: boolean): void {
    this.showGrid = show;
    this.drawBackdrop();
  }

  // ------------------------------------------------------------------ camera

  fit(): void {
    this.camera.fit();
    this.applyCamera();
  }

  applyCamera(): void {
    this.world.position.set(this.camera.x, this.camera.y);
    this.world.scale.set(this.camera.scale);
  }

  resize(): void {
    if (this.disposed) return;
    const width = this.host.clientWidth;
    const height = this.host.clientHeight;
    if (width <= 0 || height <= 0) return;

    this.app.renderer.resize(width, height);
    const wasFitted = Math.abs(this.camera.scale - this.camera.fitScale) < 0.001;
    this.camera.setViewport(width, height);
    // Keep a fitted board fitted through rotation and viewport changes; otherwise preserve
    // the player's own zoom.
    if (wasFitted) this.camera.fit();
    this.applyCamera();
  }

  destroy(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.resizeObserver?.disconnect();
    this.app.canvas.removeEventListener('webglcontextlost', this.handleContextLost);
    this.exits.length = 0;
    this.flashes.clear();
    this.app.destroy(true, { children: true });
  }
}
